import "@emotion/react";
import type { Theme as BaseTheme } from "styled-system";
import expressionToWidth from "./utils/helpers";
import pxToEm from "./utils/pxToEm";
import pxToRem from "./utils/pxToRem";

const makeTheme = <T extends BaseTheme>(t: T) => t;

type SpaceTokensMap = {
  [key: string]: string;
};

function generateSpaces(
  start = 0,
  tokensMap: SpaceTokensMap = { auto: "auto", "sp-0": "0", "-sp-0": "0" }
): SpaceTokensMap {
  if (start === 80) return tokensMap;
  let next;
  if (start < 4) {
    next = start + 2;
  } else if (start < 24) {
    next = start + 4;
  } else {
    next = start + 8;
  }
  let key: keyof SpaceTokensMap = `sp-${next}`;
  let negativeKey: keyof SpaceTokensMap = `-sp-${next}`;
  let value = pxToRem(next);
  let negativeValue = pxToRem(0 - next);
  tokensMap[key] = value;
  tokensMap[negativeKey] = negativeValue;
  return generateSpaces(next, tokensMap);
}

const theme = makeTheme({
  space: Object.assign(generateSpaces(), {
    accordionIconMarginTop: 0,
    accordionPaddingY: pxToRem(32),

    btn32PaddingTop: pxToRem(4),
    btn32PaddingX: pxToRem(16),
    btn32PaddingBottom: pxToRem(5),

    btn40PaddingTop: pxToRem(7),
    btn40PaddingX: pxToRem(24),
    btn40PaddingBottom: pxToRem(7),

    btn48PaddingTop: pxToRem(11),
    btn48PaddingX: pxToRem(31),
    btn48PaddingBottom: pxToRem(11),

    btn56PaddingTop: pxToRem(13),
    btn56PaddingX: pxToRem(40),
    btn56PaddingBottom: pxToRem(13),

    btnBorderlessPaddingX: "0",

    contentPaddingX: pxToRem(22),
    tabSize1PaddingTop: pxToRem(3),
    tabSize1PaddingX: pxToRem(11),
    tabSize1PaddingBottom: pxToRem(3),

    tabSize2PaddingTop: pxToRem(3),
    tabSize2PaddingX: pxToRem(11),
    tabSize2PaddingBottom: pxToRem(3),

    tabSize3PaddingTop: pxToRem(3),
    tabSize3PaddingX: pxToRem(11),
    tabSize3PaddingBottom: pxToRem(3),

    tabSize4PaddingTop: pxToRem(3),
    tabSize4PaddingX: pxToRem(11),
    tabSize4PaddingBottom: pxToRem(3),

    checkboxTickLeft: "0.25rem",
    checkboxTickTop: "0.3rem",

    fieldPaddingX: pxToRem(16),
    fieldPaddingY: pxToRem(11),

    errorIconMarginRight: pxToRem(8),
    errorIconMarginTop: pxToRem(-3),

    labelledFieldMarginTop: pxToRem(8),

    calendarDayNamesPb: pxToRem(8),

    datepickerSpace: pxToRem(8),
    "-datepickerSpace": pxToRem(-8),
    datepickerHintMarginBottom: pxToRem(8),

    /* Modal */
    /* modal header */
    modalHeaderPaddingTopSmall: pxToRem(16),
    modalHeaderPaddingTop: pxToRem(32),

    modalHeaderPaddingRightSmall: pxToRem(48),
    modalHeaderPaddingRight: pxToRem(56),

    modalHeaderPaddingBottomSmall: pxToRem(16),
    modalHeaderPaddingBottom: pxToRem(24),

    modalHeaderPaddingLeftSmall: pxToRem(16),
    modalHeaderPaddingLeft: pxToRem(40),

    /* modal heading / title */
    modalHeadingMarginBottom: pxToRem(8),
    modalHeadingPaddingRightSmall: 0,
    modalHeadingPaddingRight: 0,

    /* modal body */
    modalBodyPaddingTopSmall: pxToRem(24),
    modalBodyPaddingTop: pxToRem(24),

    modalBodyPaddingRightSmall: pxToRem(16),
    modalBodyPaddingRight: pxToRem(40),

    modalBodyPaddingBottomSmall: pxToRem(16),
    modalBodyPaddingBottom: pxToRem(40),

    modalBodyPaddingLeftSmall: pxToRem(16),
    modalBodyPaddingLeft: pxToRem(40),

    /* modal footer */
    modalFooterPaddingTopSmall: pxToRem(16),
    modalFooterPaddingTop: pxToRem(20),

    modalFooterPaddingRightSmall: pxToRem(16),
    modalFooterPaddingRight: pxToRem(40),

    modalFooterPaddingBottomSmall: pxToRem(16),
    modalFooterPaddingBottom: pxToRem(20),

    modalFooterPaddingLeftSmall: pxToRem(16),
    modalFooterPaddingLeft: pxToRem(40),

    /* modal close */
    modalCloseBtnMT: pxToRem(12),
    modalBigCloseBtnMT: pxToRem(24),
    modalCloseBtnMR: pxToRem(12),
    modalBigCloseBtnMR: pxToRem(24),

    /* InfoModal */
    /* modal header */
    infoModalHeaderPaddingTopSmall: pxToRem(16),
    infoModalHeaderPaddingTop: pxToRem(40),

    infoModalHeaderPaddingRightSmall: pxToRem(48),
    infoModalHeaderPaddingRight: pxToRem(56),

    infoModalHeaderPaddingBottomSmall: pxToRem(4),
    infoModalHeaderPaddingBottom: pxToRem(16),

    infoModalHeaderPaddingLeftSmall: pxToRem(16),
    infoModalHeaderPaddingLeft: pxToRem(40),

    infoModalHeaderSpace: pxToRem(16),

    /* modal body */
    infoModalBodySpaceSmall: pxToRem(32),
    infoModalBodySpace: pxToRem(40),

    /* Message */
    messagePaddingTopSmall: pxToRem(12),
    messagePaddingTop: pxToRem(20),

    messagePaddingRightSmall: pxToRem(16),
    messagePaddingRight: pxToRem(16),

    messagePaddingBottomSmall: pxToRem(16),
    messagePaddingBottom: pxToRem(20),

    messagePaddingLeftSmall: pxToRem(16),
    messagePaddingLeft: pxToRem(16),

    messageIconMarginBottom: pxToRem(8),
    messageIconMarginRight: pxToRem(16),

    messageCloseTop: 6,
    messageCloseTopBig: 12,
    messageCloseRight: 8,
    messageCloseRightBig: 12,

    tooltipPaddingX: pxToRem(12),
    tooltipPaddingY: pxToRem(12),

    comboboxListPadding: pxToRem(8),

    /* FileUploader */
    fileCardPaddingTop: pxToRem(12),
    fileCardPaddingRight: pxToRem(16),
    fileCardPaddingBottom: pxToRem(12),
    fileCardPaddingLeft: pxToRem(16),
    fileCancelBtnIconOffset: pxToRem(8),
    fileRemoveBtnIconOffset: pxToRem(8),

    fileUploaderIconTopOffset: "0",

    /* Stepper */
    stepperFieldPadding: pxToRem(3),
    stepperButtonPadding: pxToRem(9),
    stepperFieldInputPaddingY: pxToRem(7),
    stepperFieldInputPaddingX: pxToRem(15),

    /* Headings */
    h1MarginTop: "1.5em",
    h1BigMarginTop: "1.5em",
    h1MarginBottom: "1em",
    h1BigMarginBottom: "1em",

    h2MarginTop: "1.5em",
    h2BigMarginTop: "1.5em",
    h2MarginBottom: "1em",
    h2BigMarginBottom: "1em",

    h3MarginTop: "1.5em",
    h3BigMarginTop: "1.5em",
    h3MarginBottom: "1em",
    h3BigMarginBottom: "1em",

    h4MarginTop: "1.5em",
    h4BigMarginTop: "1.5em",
    h4MarginBottom: "1em",
    h4BigMarginBottom: "1em",

    h5MarginTop: "1.5em",
    h5BigMarginTop: "1.5em",
    h5MarginBottom: "1em",
    h5BigMarginBottom: "1em",

    h6MarginTop: "1.5em",
    h6BigMarginTop: "1.5em",
    h6MarginBottom: "1em",
    h6BigMarginBottom: "1em",

    /* Navigation */
    navLinkSize1PaddingTop: pxToRem(4),
    navLinkSize1PaddingX: pxToRem(12),
    navLinkSize1PaddingBottom: pxToRem(2),

    navLinkSize2PaddingTop: pxToRem(4),
    navLinkSize2PaddingX: pxToRem(12),
    navLinkSize2PaddingBottom: pxToRem(2),
  }) as Record<string | number, string>,
  colors: {
    brand: "#cc2222",
    body: "#CED0D2",
    focus: "#6688DD",
    transparent: "rgba(255, 255, 255, 0)",

    white: "#fff",
    "gray-05": "#F3F5F7",
    "gray-10": "#EEEFF2",
    "gray-20": "#DFE1E6",
    "gray-30": "#C4C8D0",
    "gray-40": "#B2B5BB",
    "gray-60": "#696C71",
    "gray-80": "#3C3E41",
    "gray-90": "#0E0E0F",
    black: "#000",

    "blue-05": "#EBF1FF",
    "blue-10": "#DDE7FF",
    "blue-20": "#BBCFFF",
    "blue-30": "#8FB4FF",
    "blue-60": "#0044CC",
    "blue-70": "#0033AA",
    "blue-80": "#002277",

    "success-05": "#E9FCF3",
    "success-10": "#CAF7D9",
    "success-20": "#A1EBB3",
    "success-30": "#81DE98",
    "success-60": "#008844",
    "success-70": "#006633",
    "success-80": "#003311",

    "warning-05": "#FFF7E6",
    "warning-10": "#FFF1CC",
    "warning-20": "#FFE8AA",
    "warning-30": "#FFDE77",
    "warning-60": "#FFAA00",
    "warning-70": "#EE8800",
    "warning-80": "#CC6600",

    "error-05": "#FFF2F2",
    "error-10": "#FFDEDD",
    "error-20": "#FFC3BB",
    "error-30": "#FFA28F",
    "error-60": "#AA0000",
    "error-70": "#880000",
    "error-80": "#660000",

    "biege-05": "#F7F5EE",
    "biege-25": "#E1D0B9",
    "biege-60": "#C5AD8D",
    "biege-80": "#5B422C",

    "green-05": "#F2FDA5",
    "green-25": "#CFDE64",
    "green-60": "#B7CC1A",

    "red-05": "#FFD8D8",
    "red-25": "#FFABAB",
    "blue-25": "#8AC0FF",

    /* purple */
    "purple-60": "#773388",
    "purple-70": "#552266",
    "purple-80": "#331144",

    /* Accordion */
    accordionColorText: "#0E0E0F",
    accordionColorHoverText: "#696C71",
    accordionColorActiveText: "#3C3E41",
    accordionColorBorder: "#DFE1E6",

    /* Button Primary */
    primaryBtnBg: "#0044CC",
    primaryBtnBorder: "#0044CC",
    primaryBtnText: "white",

    primaryBtnHoverBg: "#0033AA",
    primaryBtnHoverBorder: "#0033AA",
    primaryBtnHoverText: "white",

    primaryBtnActiveBg: "#002277",
    primaryBtnActiveBorder: "#002277",
    primaryBtnActiveText: "white",

    primaryBtnDisabledBg: "#EEEFF2",
    primaryBtnDisabledBorder: "#EEEFF2",
    primaryBtnDisabledBText: "#696C71",

    /* Button Secondary */
    secondaryBtnBg: "#DDE7FF",
    secondaryBtnBorder: "#DDE7FF",
    secondaryBtnText: "#0033AA",

    secondaryBtnHoverBg: "#BBCFFF",
    secondaryBtnHoverBorder: "#BBCFFF",
    secondaryBtnHoverText: "#0033AA",

    secondaryBtnActiveBg: "#8FB4FF",
    secondaryBtnActiveBorder: "#8FB4FF",
    secondaryBtnActiveText: "#0033AA",

    secondaryBtnDisabledBg: "#EEEFF2",
    secondaryBtnDisabledBorder: "#EEEFF2",
    secondaryBtnDisabledBText: "#696C71",

    /* Button Tertiary */
    tertiaryBtnBg: "white",
    tertiaryBtnBorder: "#C4C8D0",
    tertiaryBtnText: "#0E0E0F",

    tertiaryBtnHoverBg: "#F3F5F7",
    tertiaryBtnHoverBorder: "#C4C8D0",
    tertiaryBtnHoverText: "#0E0E0F",

    tertiaryBtnActiveBg: "#EEEFF2",
    tertiaryBtnActiveBorder: "#C4C8D0",
    tertiaryBtnActiveText: "#0E0E0F",

    tertiaryBtnDisabledBg: "#EEEFF2",
    tertiaryBtnDisabledBorder: "#C4C8D0",
    tertiaryBtnDisabledBText: "#696C71",

    /* Button Borderless */
    borderlessBtnBg: "rgba(255, 255, 255, 0)",
    borderlessBtnBorder: "rgba(255, 255, 255, 0)",
    borderlessBtnText: "#0044CC",

    borderlessBtnHoverBg: "rgba(255, 255, 255, 0)",
    borderlessBtnHoverBorder: "rgba(255, 255, 255, 0)",
    borderlessBtnHoverText: "#0033AA",

    borderlessBtnActiveBg: "rgba(255, 255, 255, 0)",
    borderlessBtnActiveBorder: "rgba(255, 255, 255, 0)",
    borderlessBtnActiveText: "#002277",

    borderlessBtnDisabledBg: "rgba(255, 255, 255, 0)",
    borderlessBtnDisabledBorder: "rgba(255, 255, 255, 0)",
    borderlessBtnDisabledBText: "#696C71",

    /* Field */
    fieldBorderColor: "#C4C8D0",
    fieldBgColor: "white",
    fieldTextColor: "#0E0E0F",
    fieldPlaceholderColor: "#696C71",

    fieldHoverBorderColor: "#B2B5BB",
    fieldHoverBgColor: "white",

    fieldFocusBorderColor: "#0044CC",

    fieldErrorBorderColor: "#AA0000",
    fieldErrorBgColor: "white",

    fieldErrorHoverBorderColor: "#880000",

    fieldDisabledBorderColor: "#C4C8D0",
    fieldDisabledBgColor: "#EEEFF2",
    fieldDisabledTextColor: "#696C71",

    labelColor: "#0E0E0F",
    errorColor: "#AA0000",
    hintColor: "#3C3E41",

    /* Checkables */
    checkableBgColor: "white",
    checkableBorderColor: "#C4C8D0",
    checkableLabelColor: "#0E0E0F",
    checkableHoverBgColor: "white",
    checkableHoverBorderColor: "#0033AA",
    checkableActiveBgColor: "white",
    checkableActiveBorderColor: "#002277",

    checkableTickColor: "white",

    checkableCheckedColor: "#0044CC",
    checkableCheckedHoverColor: "#0033AA",
    checkableCheckedActiveColor: "#002277",

    checkableErrorColor: "#AA0000",
    checkableErrorHoverColor: "#880000",
    checkableErrorActiveColor: "#660000",

    checkableDisabledBgColor: "#EEEFF2",
    checkableDisabledCheckedBgColor: "#C4C8D0",
    checkableDisabledBorderColor: "#C4C8D0",
    checkableDisabledTickColor: "white",
    checkableDisabledText: "#696C71",
    checkableDisabledLabelColor: "#696C71",

    /* Calendar */
    calendarDayOfWeek: "#696C71",

    calendarBtnBorderColor: "#DFE1E6",
    calendarBtnBg: "white",
    calendarBtnHoverBg: "#DDE7FF",
    calendarBtnActiveBg: "#BBCFFF",
    calendarBtnText: "#0E0E0F",

    calendarBtnSelectedBg: "#0044CC",
    calendarBtnSelectedHoverBg: "#0044CC",
    calendarBtnSelectedActiveBg: "#0044CC",
    calendarSelectedText: "white",

    calendarIntervalBg: "#BBCFFF",
    calendarIntervalHoverBg: "#BBCFFF",
    calendarIntervalActiveBg: "#BBCFFF",

    calendarBtnDisabledBg: "#EEEFF2",
    calendarBtnDisabledText: "#696C71",

    /* Datepicker */
    datepickerCalendarBtnBg: "transparent",
    datepickerCalendarBtnColor: "#696C71",

    datepickerCalendarBtnHoverBg: "transparent",
    datepickerCalendarBtnHoverColor: "#3C3E41",

    datepickerCalendarBtnActiveBg: "transparent",
    datepickerCalendarBtnActiveColor: "#0E0E0F",

    datepickerCalendarBtnDisabledBg: "transparent",
    datepickerCalendarBtnDisabledColor: "#B2B5BB",

    datepickerButtonTertiaryEnable: "#C4C8D0",
    datepickerButtonTertiaryHover: "#C4C8D0",
    datepickerButtonTertiaryActive: "#C4C8D0",
    datepickerButtonTertiaryDisable: "#C4C8D0",

    /* Divider */
    dividerBorderColor: "#EEEFF2",

    /* Link */
    linkColor: "#0044CC",
    linkHoverColor: "#0033AA",
    linkActiveColor: "#002277",
    linkVisitedColor: "#773388",
    linkVisitedActiveColor: "#331144",
    linkVisitedHoverColor: "#552266",

    /* Breadcrumbs */
    breadcrumbsColor: "#696C71",
    breadcrumbsHoverColor: "#3C3E41",
    breadcrumbsActiveColor: "#0E0E0F",

    /* Popover */
    popoverBorderColor: "#DFE1E6",
    popoverInfoColor: "#C4C8D0",
    popoverInfoHoverColor: "#B2B5BB",
    popoverInfoActiveColor: "#696C71",

    /* Modal */
    modalDividerColor: "#DFE1E6",

    modalCloseButtonColor: "#3C3E41",
    modalCloseButtonBg: "#EEEFF2",
    modalCloseButtonHoverBg: "#DFE1E6",
    modalCloseButtonActiveBg: "#C4C8D0",

    /* InfoModal */
    "infoModal-error-InternalRingColor": "#FFC3BB",
    "infoModal-error-ExternalRingColor": "#FFDEDD",
    "infoModal-error-Color": "#AA0000",
    "infoModal-warning-InternalRingColor": "#FFE8AA",
    "infoModal-warning-ExternalRingColor": "#FFF1CC",
    "infoModal-warning-Color": "#FFAA00",
    "infoModal-success-InternalRingColor": "#A1EBB3",
    "infoModal-success-ExternalRingColor": "#CAF7D9",
    "infoModal-success-Color": "#008844",

    /* Message */
    messageErrorBg: "#FFF2F2",
    messageErrorBorderColor: "transparent",
    messageErrorBorderLeftColor: "#AA0000",

    messageWarningBg: "#FFF7E6",
    messageWarningBorderColor: "transparent",
    messageWarningBorderLeftColor: "#FFAA00",

    messageSuccessBg: "#E9FCF3",
    messageSuccessBorderColor: "transparent",
    messageSuccessBorderLeftColor: "#008844",

    messageInfoBg: "#EBF1FF",
    messageInfoBorderColor: "transparent",
    messageInfoBorderLeftColor: "#0044CC",

    /* Pagination */
    pageColor: "#0E0E0F",
    pageBg: "white",
    pageHoverBg: "#DDE7FF",
    pageActiveBg: "#BBCFFF",
    pageCurrentBg: "#DDE7FF",
    pageCurrentHoverBg: "#DDE7FF",
    pageCurrentActiveBg: "#DDE7FF",
    tooltipBg: "#3C3E41",

    /* ProgressBar */
    progressBarBg: "#EEEFF2",
    progressBarValue: "#008844",

    /* Combobox */
    comboboxOptionBg: "white",
    comboboxOptionHoverBg: "#DDE7FF",
    comboboxOptionActiveBg: "#BBCFFF",
    comboboxOptionSelectedBg: "#8FB4FF",

    /* Loader */
    loaderColor: "#696C71",

    /* Scroll */
    scrollColor: "#696C71",

    /* Stepper */
    stepperColor: "#0E0E0F",
    stepperDisabledColor: "#696C71",

    stepperBg: "white",
    stepperButtonHoverBg: "#F3F5F7",
    stepperInputHoverBg: "#FFF",
    stepperActiveBg: "#EEEFF2",
    stepperDisabledBg: "#EEEFF2",

    /* FileUploader */
    fileCardBorderColor: "#C4C8D0",
    fileUploaderSpinnerColor: "#0044CC",
    fileIconColor: "#0044CC",

    /* Navigation */
    navListBorderBottomColor: "#DFE1E6",
    /* item-1 */
    navLinkItem1TextColor: "#0E0E0F",
    navLinkItem1BorderBottomColor: "transparent",
    navLinkItem1HoverBorderBottomColor: "#DFE1E6",
    navLinkItem1ActiveBorderBottomColor: "#DFE1E6",

    navLinkItem1SelectedTextColor: "#0E0E0F",
    navLinkItem1SelectedBorderBottomColor: "#CC2222",

    /* item-2 */
    navLinkItem2TextColor: "#0E0E0F",
    navLinkItem2BorderBottomColor: "transparent",
    navLinkItem2HoverBorderBottomColor: "#DFE1E6",
    navLinkItem2ActiveBorderBottomColor: "#DFE1E6",

    navLinkItem2SelectedTextColor: "#0E0E0F",
    navLinkItem2SelectedBorderBottomColor: "#0E0E0F",

    /* item-3 */
    navLinkItem3TextColor: "#0E0E0F",
    navLinkItem3BorderBottomColor: "transparent",
    navLinkItem3HoverBorderBottomColor: "#DFE1E6",
    navLinkItem3ActiveBorderBottomColor: "#DFE1E6",

    navLinkItem3SelectedTextColor: "#0044CC",
    navLinkItem3SelectedBorderBottomColor: "#0044CC",

    /* Tabs */
    /* Tab-1 */
    tab1Bg: "#F3F5F7",
    tab1TextColor: "#0E0E0F",
    tab1BorderColor: "#F3F5F7",

    tab1HoverBg: "#EEEFF2",
    tab1HoverBorderColor: "#EEEFF2",
    tab1HoverTextColor: "#0E0E0F",

    tab1ActiveBg: "#DFE1E6",
    tab1ActiveBorderColor: "#DFE1E6",
    tab1ActiveTextColor: "#0E0E0F",

    tab1DisabledBg: "#EEEFF2",
    tab1DisabledBorderColor: "#EEEFF2",
    tab1DisabledTextColor: "#959EAB",

    /* Tab-1 selected */
    tab1SelectedTextColor: "#13151A",

    tab1SelectedFocusBg: "#FFFFFF",
    tab1SelectedFocusBorderColor: "#FFFFFF",

    tab1SelectedHoverBg: "#FFFFFF",
    tab1SelectedHoverBorderColor: "#FFFFFF",
    tab1SelectedHoverTextColor: "#696C71",

    tab1SelectedActiveBg: "#FFFFFF",
    tab1SelectedActiveBorderColor: "#FFFFFF",
    tab1SelectedActiveTextColor: "#3C3E41",

    tab1SelectedDisabledBg: "#FFFFFF",
    tab1SelectedDisabledBorderColor: "#FFFFFF",
    tab1SelectedDisabledTextColor: "#696C71",

    /* Tab-2 */
    tab2Bg: "#FFFFFF",
    tab2TextColor: "#0E0E0F",
    tab2BorderColor: "#FFFFFF",

    tab2HoverBg: "#F3F5F7",
    tab2HoverBorderColor: "#F3F5F7",
    tab2HoverTextColor: "#0E0E0F",

    tab2ActiveBg: "#EEEFF2",
    tab2ActiveBorderColor: "#EEEFF2",
    tab2ActiveTextColor: "#0E0E0F",

    tab2DisabledBg: "#FFFFFF",
    tab2DisabledBorderColor: "#FFFFFF",
    tab2DisabledTextColor: "#696C71",

    /* Tab-2 selected */
    tab2SelectedTextColor: "#0E0E0F",

    tab2SelectedFocusBg: "#EEEFF2",
    tab2SelectedFocusBorderColor: "#EEEFF2",

    tab2SelectedHoverBg: "#DFE1E6",
    tab2SelectedHoverBorderColor: "#DFE1E6",
    tab2SelectedHoverTextColor: "#0E0E0F",

    tab2SelectedActiveBg: "#C4C8D0",
    tab2SelectedActiveBorderColor: "#C4C8D0",
    tab2SelectedActiveTextColor: "#0E0E0F",

    tab2SelectedDisabledBg: "#EEEFF2",
    tab2SelectedDisabledBorderColor: "#EEEFF2",
    tab2SelectedDisabledTextColor: "#696C71",

    /* Tab-3 */
    tab3Bg: "#DDE7FF",
    tab3TextColor: "#0033AA",
    tab3BorderColor: "#DDE7FF",

    tab3HoverBg: "#BBCFFF",
    tab3HoverBorderColor: "#BBCFFF",
    tab3HoverTextColor: "#0033AA",

    tab3ActiveBg: "#8FB4FF",
    tab3ActiveBorderColor: "#8FB4FF",
    tab3ActiveTextColor: "#0033AA",

    tab3DisabledBg: "#EEEFF2",
    tab3DisabledBorderColor: "#EEEFF2",
    tab3DisabledTextColor: "#696C71",

    /* Tab-3 selected */
    tab3SelectedTextColor: "#FFFFFF",

    tab3SelectedFocusBg: "#0044CC",
    tab3SelectedFocusBorderColor: "#0044CC",

    tab3SelectedHoverBg: "#0033AA",
    tab3SelectedHoverBorderColor: "#0033AA",
    tab3SelectedHoverTextColor: "#FFFFFF",

    tab3SelectedActiveBg: "#002277",
    tab3SelectedActiveBorderColor: "#002277",
    tab3SelectedActiveTextColor: "#FFFFFF",

    tab3SelectedDisabledBg: "#EEEFF2",
    tab3SelectedDisabledBorderColor: "#EEEFF2",
    tab3SelectedDisabledTextColor: "#696C71",
  } as Record<string, string>,
  fonts: {
    main: "'Golos Text', sans-serif",
    serif: "'PT Serif', serif",
    ui: "'Golos Text', sans-serif",

    headings: "'Golos Text', sans-serif",
  } as Record<string, string>,
  fontSizes: {
    "fs-14": pxToRem(14),
    "fs-15": pxToRem(15),
    "fs-16": pxToRem(16),
    "fs-18": pxToRem(18),
    "fs-21": pxToRem(21),
    "fs-22": pxToRem(22),
    "fs-24": pxToRem(24),
    "fs-26": pxToRem(26),
    "fs-32": pxToRem(32),

    textBase: pxToRem(16),
    textSmall: pxToRem(14),
    textBig: pxToRem(18),

    h1: pxToRem(26),
    h1Big: pxToRem(32),

    h2: pxToRem(24),
    h2Big: pxToRem(26),

    h3: pxToRem(21),
    h3Big: pxToRem(22),

    h4: pxToRem(18),
    h4Big: pxToRem(18),

    h5: pxToRem(16),
    h5Big: pxToRem(16),

    h6: pxToRem(15),
    h6Big: pxToRem(15),

    btn32FontSize: pxToRem(14),
    btn40FontSize: pxToRem(16),
    btn48FontSize: pxToRem(16),
    btn56FontSize: pxToRem(18),

    errorFontSize: pxToRem(16),
    hintFontSize: pxToRem(14),

    paginationFontSize: pxToRem(16),

    fileUploaderMessageFontSize: pxToRem(14),

    /* Navigation */
    navLinkSize1FontSize: pxToRem(14),
    navLinkSize2FontSize: pxToRem(16),

    /* tabs */
    defaultTabSize1FontSize: pxToRem(14),
    defaultTabSize2FontSize: pxToRem(14),
    defaultTabSize3FontSize: pxToRem(16),
    defaultTabSize4FontSize: pxToRem(16),

    tab1Size1FontSize: pxToRem(14),
    tab1Size2FontSize: pxToRem(14),
    tab1Size3FontSize: pxToRem(14),
    tab1Size4FontSize: pxToRem(16),
  } as Record<string, string>,
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,

    h1FontWeight: 700,
    h2FontWeight: 600,
    h3FontWeight: 600,
    h4FontWeight: 600,
    h5FontWeight: 600,
    h6FontWeight: 600,

    btn32FontWeight: 500,
    btn40FontWeight: 500,
    btn48FontWeight: 500,
    btn56FontWeight: 500,

    labelFontWeight: 600,

    /* Navigation */
    navLinkItem1FontWeight: 400,
    navLinkItem2FontWeight: 400,
    navLinkItem3FontWeight: 400,

    navLinkItem1SelectedFontWeight: 500,
    navLinkItem2SelectedFontWeight: 500,
    navLinkItem3SelectedFontWeight: 500,

    /* Tabs */

    tab1FontWeight: 400,
    tab2FontWeight: 400,
    tab3FontWeight: 400,

    /* Tab selected*/
    tab1SelectedFontWeight: 500,
    tab2SelectedFontWeight: 500,
    tab3SelectedFontWeight: 500,
  },
  lineHeights: {
    "lh-12": 1.2,
    "lh-15": 1.5,

    textBaseLineHeight: 1.5,
    textSmallLineHeight: 1.5,
    textBigLineHeight: 1.5,

    h1LineHeight: 1.2,
    h1BigLineHeight: 1.2,

    h2LineHeight: 1.2,
    h2BigLineHeight: 1.2,

    h3LineHeight: 1.2,
    h3BigLineHeight: 1.2,

    h4LineHeight: 1.17,
    h4BigLineHeight: 1.2,

    h5LineHeight: 1.2,
    h5BigLineHeight: 1.2,

    h6LineHeight: 1.2,
    h6BigLineHeight: 1.2,

    btn32LineHeight: 1.5,
    btn40LineHeight: 1.5,
    btn48LineHeight: 1.5,
    btn56LineHeight: 1.55,

    tabSize1LineHeight: 1.145,
    tabSize2LineHeight: 1.145,
    tabSize3LineHeight: 1.145,
    tabSize4LineHeight: 1.145,

    calendarBtnLineHeight: 1.43,

    labelLineHeight: 1.5,
    checkableLabelLineHeight: 1.5,
    errorLineHeight: 1.5,
    hintLineHeight: 1.5,

    paginationLineHeight: 1.5,

    /* Navigation */
    navLinkSize1LineHeight: 1.145,
    navLinkSize2LineHeight: 1.25,
  } as Record<string, number>,
  letterSpacings: {
    h1LetterSpacing: "0",
    h1BigLetterSpacing: "0",

    h2LetterSpacing: "0",
    h2BigLetterSpacing: "0",

    h3LetterSpacing: "0",
    h3BigLetterSpacing: "0",

    h4LetterSpacing: "0",
    h4BigLetterSpacing: "0",

    h5LetterSpacing: "0",
    h5BigLetterSpacing: "0",

    h6LetterSpacing: "0",
    h6BigLetterSpacing: "0",
  },
  borders: {
    0: `0 solid currentColor`,
    1: `${pxToRem(1)} solid currentColor`,
    2: `${pxToRem(2)} solid currentColor`,
    4: `${pxToRem(4)} solid currentColor`,
  } as Record<0 | 1 | 2 | 4 | "0" | "1" | "2" | "4", string>,
  borderWidths: {
    "0": "0",
    "1": pxToRem(1),
    "2": pxToRem(2),
    "4": pxToRem(4),

    checkboxTickBorderWidth: `0 0 ${pxToRem(2)} ${pxToRem(2)}`,
    radioTickBorderWidth: pxToRem(4.5),

    messageBorderWidth: pxToRem(1),
    messageBorderLeftWidth: pxToRem(4),

    modalHeaderBorder: pxToRem(1),

    /* Navigation */
    navLinkItem1BorderWidth: 0,
    navLinkItem2BorderWidth: 0,
    navLinkItem3BorderWidth: 0,
    navLinkItem1BorderBottomWidth: pxToRem(2),
    navLinkItem2BorderBottomWidth: pxToRem(2),
    navLinkItem3BorderBottomWidth: pxToRem(2),

    /* Tabs */
    tab1BorderWidth: pxToRem(1),
    tab2BorderWidth: pxToRem(1),
    tab3BorderWidth: pxToRem(1),
  },
  radii: {
    0: "0",
    2: pxToRem(2),
    4: pxToRem(4),
    accordion: pxToRem(2),
    round: "50%",
    button: pxToRem(4),
    starBorderRadius: pxToRem(4),
    checkboxBorderRadius: pxToRem(4),
    stepperBorderRadius: pxToRem(4),
    stepperButtonBorderRaduis: pxToRem(4),
    fieldBorderRadius: pxToRem(4),
    popover: pxToRem(4),
    breadCrumb: "0",
    linkFocusBorderRadius: pxToRem(4),
    modal: "0",
    modalBig: pxToRem(8),
    infomodal: pxToRem(8),
    modalClose: "50%",
    messageBorderRadius: pxToRem(2),
    paginationBorderRadius: pxToRem(4),
    tooltip: pxToRem(4),

    fileCard: pxToRem(4),

    /* Tabs */
    tab1BorderRadius: pxToRem(6),
    tab2BorderRadius: pxToRem(6),
    tab3BorderRadius: pxToRem(6),
  } as Record<0 | 2 | 4 | "0" | "2" | "4" | "round" | string, string>,
  shadows: {
    4: "0px 2px 4px rgba(0, 20, 67, 0.08)",
    12: "0px 4px 12px rgba(0, 20, 67, 0.12)",
    32: "0px 16px 32px rgba(0, 20, 67, 0.12)",
    focus: "0 0 0 0.1875rem #6688DD",
    none: "none",
    popover: "0px 4px 12px rgba(0, 20, 67, 0.12)",
    modal: "0px 16px 32px rgba(0, 20, 67, 0.12)",
  } as Record<4 | 12 | 32 | "4" | "12" | "32" | "focus" | "none", string>,
  sizes: {
    auto: "auto",
    0: 0,
    "1": expressionToWidth("1/1"),
    "1/2": expressionToWidth("1/2"),
    "1/3": expressionToWidth("1/3"),
    "2/3": expressionToWidth("1/2"),
    "1/4": expressionToWidth("1/4"),
    "3/4": expressionToWidth("3/4"),
    "1/5": expressionToWidth("1/5"),
    "2/5": expressionToWidth("2/5"),
    "3/5": expressionToWidth("3/5"),
    "4/5": expressionToWidth("4/5"),
    "inner-content": pxToRem(832),
    "semi-wide": pxToRem(724),

    btn32IconSize: "1.5em",
    btn40IconSize: "1.5em",
    btn48IconSize: "1.5em",
    btn56IconSize: "1.5em",

    tabSize1IconSize: pxToRem(16),
    tabSize2IconSize: pxToRem(16),
    tabSize3IconSize: pxToRem(16),
    tabSize4IconSize: pxToRem(16),

    /* Accordion */
    accordionMinWidth: "auto",

    /* Radiobutton & Checkbox */
    checkableSize: pxToRem(20),

    radioTickSize: pxToRem(8),

    checkboxTickWidth: pxToRem(10),
    checkboxTickHeight: pxToRem(6),

    checkBoxIndeterminateWidth: pxToRem(10),
    checkBoxIndeterminateHeight: pxToRem(0),

    /* Calendar */
    calendarNavBtn: pxToRem(32),
    calendarNavIcon: pxToRem(21),

    datepickerBtnIconSize: pxToRem(24),
    datepickerSingleFieldWidth: pxToRem(160),
    datepickerDayMonthFieldWidth: pxToRem(58),
    datepickerYearFieldWidth: pxToRem(98),

    /* Modal */
    modalSmallMaxWidth: pxToRem(512),
    modalMaxWidth: pxToRem(724),
    modalMinHeight: "100%",
    modalMaxHeight: "100%",
    modalMaxHeightBig: "100%",

    modalCloseButtonSize: pxToRem(32),
    modalBigCloseButtonSize: pxToRem(32),

    /* InfoModal */
    infoModalMaxWidth: pxToRem(540),
    infoModalIconBlockSmall: pxToRem(40),
    infoModalIconBlock: pxToRem(40),
    infoModalIconSizeSmall: pxToRem(22),
    infoModalIconSize: pxToRem(22),

    messageIcon: pxToRem(20),
    messageCloseBtn: pxToRem(32),
    messageCloseBtnBig: pxToRem(32),

    /* FileUploader */
    fileUploaderMaxWidth: pxToRem(644),
    fileUploadBtnWidth: pxToRem(216),
    fileUploaderBtnIconSize: "1.5em",
    fileUploaderBtnIconSizeBig: "1.5em",
    fileUploaderSpinnerSize: pxToRem(24),
    fileUploaderFileIconSize: pxToRem(24),
    fileUploaderErrorIconSize: pxToRem(20),

    fileUploaderIconBlockWidth: pxToRem(24),

    /* Navigation */
    navLinkSize1MinHeight: pxToRem(40),
    navLinkSize1IconSize: "1.5em",

    navLinkSize2MinHeight: pxToRem(48),
    navLinkSize2IconSize: "1.6em",

    /* Tabs */
    defaultTabSize1MinHeight: pxToRem(32),
    defaultTabSize2MinHeight: pxToRem(40),
    defaultTabSize3MinHeight: pxToRem(48),
    defaultTabSize4MinHeight: pxToRem(56),

    tab1Size1MinHeight: pxToRem(24),
    tab1Size2MinHeight: pxToRem(32),
    tab1Size3MinHeight: pxToRem(40),
    tab1Size4MinHeight: pxToRem(48),
  } as Record<string, string | number>,
  zIndices: {
    0: 0,
    1: 1,
    1010: 1010,
  } as Record<0 | 1 | 1010 | "0" | "1" | "1010", number>,
  breakpoints: [pxToEm(768), pxToEm(1024), pxToEm(1280), pxToEm(1366), pxToEm(1600)],
});

export type ThemeType = typeof theme;

declare module "@emotion/react" {
  export interface Theme extends ThemeType {}
}

/**
 * @deprecated Основная тема — theme30.
 */
export { theme };
